import { Alert as RNAlert, Platform } from 'react-native';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';

import { getCurrentAppTheme } from '../../theme';

const WebAlertModal = ({ title, message, buttons, options, onClose }) => {
	const firstButtonRef = useRef(null);
	// Rendered in its own React root, outside the theme provider.
	const { colors, radius, touch, type } = getCurrentAppTheme().ui;

	useEffect(() => {
		// Focus first button when modal opens
		if (firstButtonRef.current) {
			firstButtonRef.current.focus();
		}
	}, []);

	const handleButtonClick = (button) => {
		onClose();
		if (button.onPress) {
			button.onPress();
		}
	};

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget && (!options || options.cancelable !== false)) {
			onClose();
			const cancelButton = buttons.find(b => b.style === 'cancel');
			if (cancelButton && cancelButton.onPress) {
				cancelButton.onPress();
			}
		}
	};

	const getButtonStyle = (buttonStyle) => {
		const baseStyle = {
			minHeight: `${touch.min}px`,
			padding: '8px 18px',
			border: 'none',
			borderRadius: `${radius.pill}px`,
			fontSize: `${type.bodySmallStrong.fontSize}px`,
			fontFamily: type.bodySmallStrong.fontFamily,
			fontWeight: type.bodySmallStrong.fontWeight,
			cursor: 'pointer',
			transition: 'opacity 0.2s',
		};

		switch (buttonStyle) {
			case 'cancel':
				return { ...baseStyle, backgroundColor: colors.chip, color: colors.onChip };
			case 'destructive':
				return { ...baseStyle, backgroundColor: colors.dangerSolid, color: colors.onDangerSolid };
			default:
				return { ...baseStyle, backgroundColor: colors.primary, color: colors.onPrimary };
		}
	};

	return ReactDOM.createPortal(
		<div
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: colors.overlay,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 10000,
			}}
			onClick={handleOverlayClick}
		>
			<div
				style={{
					backgroundColor: colors.surface,
					borderRadius: `${radius.hero}px`,
					padding: '20px',
					maxWidth: '400px',
					width: '90%',
					boxSizing: 'border-box',
					boxShadow: '0 4px 12px rgba(0, 0, 0, 0.16)',
				}}
			>
				{title && (
					<h3
						style={{
							margin: '0 0 10px 0',
							color: colors.text,
							fontSize: `${type.cardTitle.fontSize}px`,
							fontFamily: type.cardTitle.fontFamily,
							fontWeight: type.cardTitle.fontWeight,
						}}
					>
						{title}
					</h3>
				)}
				{message && (
					<p
						style={{
							margin: '0 0 20px 0',
							color: colors.textSecondary,
							fontSize: `${type.bodySmall.fontSize}px`,
							fontFamily: type.bodySmall.fontFamily,
							lineHeight: '1.5',
						}}
					>
						{message}
					</p>
				)}
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: '10px',
					}}
				>
					{buttons.map((button, index) => (
						<button
							key={index}
							ref={index === 0 ? firstButtonRef : null}
							style={getButtonStyle(button.style)}
							onClick={() => handleButtonClick(button)}
							onMouseEnter={(e) => {
								e.target.style.opacity = '0.85';
							}}
							onMouseLeave={(e) => {
								e.target.style.opacity = '1';
							}}
						>
							{button.text}
						</button>
					))}
				</div>
			</div>
		</div>,
		document.body,
	);
};

const alert = (title, message, buttons, options) => {
	if (Platform.OS === 'web') {
		// Use default OK button if no buttons provided
		const alertButtons = buttons || [{ text: 'OK', onPress: () => {} }];

		// Create container for React portal
		const container = document.createElement('div');
		document.body.appendChild(container);

		// Create root for React 18+
		const root = createRoot(container);

		const cleanup = () => {
			root.unmount();
			document.body.removeChild(container);
		};

		// Render the alert modal
		root.render(<WebAlertModal
			title={title}
			message={message}
			buttons={alertButtons}
			options={options}
			onClose={cleanup}
		/>);
	} else {
		// Use native Alert on mobile
		RNAlert.alert(title, message, buttons, options);
	}
};

export const Alert = {
	alert,
};
